import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  actualizarColorCurador,
  actualizarEspecieCurador,
  actualizarProductoCurador,
  actualizarRazaCurador,
  actualizarTipoVisitaCurador,
  borrarProductoCurador,
  crearColorCurador,
  crearEspecieCurador,
  crearProductoCurador,
  crearRazaCurador,
  crearTipoVisitaCurador,
  fusionarProducto,
  listarColoresCurador,
  listarEspeciesCurador,
  listarProductosCurador,
  listarRazasCurador,
  listarTiposVisitaCurador,
  obtenerCuentaActual,
  TipoProducto,
  UnidadPeriodo,
  type ColorCuradorDto,
  type EspecieCuradorDto,
  type GuardarColorRequest,
  type GuardarEspecieRequest,
  type GuardarProductoCuradorRequest,
  type GuardarRazaRequest,
  type GuardarTipoVisitaRequest,
  type ProductoCuradorDto,
  type RazaCuradorDto,
  type TipoVisitaCuradorDto,
} from "../api";
import Campo from "../componentes/Campo";
import { formatearFecha } from "../formato";

type Pestana = "productos" | "aportes" | "especies" | "catalogos";

const NOMBRES_TIPO_PRODUCTO: Record<number, string> = {
  [TipoProducto.Vacuna]: "Vacuna",
  [TipoProducto.AntiparasitarioInterno]: "Antiparasitario interno",
  [TipoProducto.AntiparasitarioExterno]: "Antiparasitario externo",
  [TipoProducto.Medicamento]: "Medicamento",
};

export default function ModuloDatos() {
  const [pestana, setPestana] = useState<Pestana>("productos");
  const cuenta = useQuery({ queryKey: ["cuenta"], queryFn: obtenerCuentaActual });

  if (cuenta.isPending) return <p className="muted">Cargando módulo de datos…</p>;
  if (!cuenta.data || (!cuenta.data.esCurador && !cuenta.data.esAdministrador)) {
    return (
      <div className="panel vacio">
        <h2>Acceso restringido</h2>
        <p className="muted">
          Este módulo está reservado para el veterinario curador y administradores del sistema.
        </p>
        <Link className="boton" to="/">
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="modulo-datos-contenedor">
      <div className="titulo-fila">
        <div>
          <h1>Módulo de datos y catálogos</h1>
          <p className="lede">
            Administración del catálogo clínico oficial, homologación de aportes y configuración de series.
          </p>
        </div>
      </div>

      <nav className="pestanas-barra">
        <button
          type="button"
          className={`pestana-boton ${pestana === "productos" ? "activa" : ""}`}
          onClick={() => setPestana("productos")}
        >
          📦 Catálogo de productos
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "aportes" ? "activa" : ""}`}
          onClick={() => setPestana("aportes")}
        >
          📥 Aportes de usuarios
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "especies" ? "activa" : ""}`}
          onClick={() => setPestana("especies")}
        >
          🐾 Especies y razas
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "catalogos" ? "activa" : ""}`}
          onClick={() => setPestana("catalogos")}
        >
          🎨 Colores y tipos de visita
        </button>
      </nav>

      {pestana === "productos" && <PestanaProductos />}
      {pestana === "aportes" && <PestanaAportes />}
      {pestana === "especies" && <PestanaEspeciesRazas />}
      {pestana === "catalogos" && <PestanaColoresYTipos />}
    </div>
  );
}

// ==========================================
// 1. PESTAÑA PRODUCTOS (Catálogo Oficial)
// ==========================================

function PestanaProductos() {
  const qc = useQueryClient();
  const [filtroTipo, setFiltroTipo] = useState<TipoProducto | "">("");
  const [filtroEspecie, setFiltroEspecie] = useState<number | "">("");
  const [busqueda, setBusqueda] = useState("");
  const [modalProducto, setModalProducto] = useState<ProductoCuradorDto | "nuevo" | null>(null);

  const especies = useQuery({ queryKey: ["especies-curador"], queryFn: listarEspeciesCurador });
  const productos = useQuery({
    queryKey: [
      "productos-curador",
      {
        tipo: filtroTipo || undefined,
        especieID: filtroEspecie || undefined,
        q: busqueda || undefined,
        soloAportes: false,
      },
    ],
    queryFn: () =>
      listarProductosCurador({
        tipo: filtroTipo ? (filtroTipo as TipoProducto) : undefined,
        especieID: filtroEspecie ? (filtroEspecie as number) : undefined,
        q: busqueda.trim() || undefined,
      }),
  });

  const mutacionBorrar = useMutation({
    mutationFn: borrarProductoCurador,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["productos-curador"] }),
  });

  return (
    <section className="panel">
      <div className="titulo-fila">
        <h2>Catálogo de productos clínicos</h2>
        <button
          type="button"
          className="boton-primario"
          onClick={() => setModalProducto("nuevo")}
        >
          + Nuevo producto oficial
        </button>
      </div>

      {/* Barra de filtros */}
      <div className="fila" style={{ marginTop: "1rem" }}>
        <Campo etiqueta="Buscar por nombre o laboratorio">
          <input
            type="text"
            placeholder="Ej: Nobivac, NexGard, Amoxicilina…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </Campo>

        <Campo etiqueta="Filtrar por tipo">
          <select
            value={filtroTipo}
            onChange={(e) =>
              setFiltroTipo(e.target.value ? (Number(e.target.value) as TipoProducto) : "")
            }
          >
            <option value="">Todos los tipos</option>
            <option value={TipoProducto.Vacuna}>Vacunas</option>
            <option value={TipoProducto.AntiparasitarioInterno}>Antiparasitarios internos</option>
            <option value={TipoProducto.AntiparasitarioExterno}>Antiparasitarios externos</option>
            <option value={TipoProducto.Medicamento}>Medicamentos</option>
          </select>
        </Campo>

        <Campo etiqueta="Filtrar por especie">
          <select
            value={filtroEspecie}
            onChange={(e) => setFiltroEspecie(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">Todas las especies</option>
            {especies.data?.map((esp) => (
              <option key={esp.especieID} value={esp.especieID}>
                {esp.nombre}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      {productos.isPending && <p className="muted" style={{ marginTop: "1rem" }}>Cargando productos…</p>}
      {productos.error && <p className="err" style={{ marginTop: "1rem" }}>{productos.error.message}</p>}

      {productos.data && (
        <div style={{ overflowX: "auto", marginTop: "1rem" }}>
          <table className="tabla-datos">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Tipo</th>
                <th>Laboratorio</th>
                <th>Especie</th>
                <th>Serie y Periodicidad</th>
                <th>Estado</th>
                <th>Usos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {productos.data.map((p) => (
                <tr key={p.productoID}>
                  <td>
                    <strong>{p.nombre}</strong>
                  </td>
                  <td>
                    <span className="etiqueta neutra">
                      {NOMBRES_TIPO_PRODUCTO[p.tipo] ?? "Otro"}
                    </span>
                  </td>
                  <td>{p.laboratorio ?? <span className="muted">—</span>}</td>
                  <td>{p.especieNombre ?? <span className="muted">Cualquiera</span>}</td>
                  <td>
                    {p.claveSerie ? (
                      <div>
                        <code>{p.claveSerie}</code>
                        {p.periodicidadCantidad && p.periodicidadUnidad && (
                          <div className="muted" style={{ fontSize: "0.8rem" }}>
                            Cada {p.periodicidadCantidad}{" "}
                            {p.periodicidadUnidad === UnidadPeriodo.Dias
                              ? "días"
                              : p.periodicidadUnidad === UnidadPeriodo.Meses
                                ? "meses"
                                : "años"}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="muted">Sin serie</span>
                    )}
                  </td>
                  <td>
                    {p.esCurado ? (
                      <span className="etiqueta neutra" style={{ color: "var(--accent)" }}>
                        Oficial
                      </span>
                    ) : (
                      <span className="etiqueta" style={{ background: "var(--alerta-suave)", color: "var(--alerta)" }}>
                        Aporte usuario
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="muted">{p.cantidadUsos} fichas</span>
                  </td>
                  <td>
                    <div className="acciones" style={{ gap: "0.3rem" }}>
                      <button
                        type="button"
                        className="boton-chico"
                        onClick={() => setModalProducto(p)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="boton-chico boton-peligro"
                        onClick={() => {
                          if (confirm(`¿Retirar '${p.nombre}' del catálogo?`)) {
                            mutacionBorrar.mutate(p.productoID);
                          }
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalProducto && (
        <ModalFormularioProducto
          producto={modalProducto === "nuevo" ? null : modalProducto}
          especies={especies.data ?? []}
          onCerrar={() => setModalProducto(null)}
        />
      )}
    </section>
  );
}

// ==========================================
// 2. PESTAÑA APORTES DE USUARIOS
// ==========================================

function PestanaAportes() {
  const qc = useQueryClient();
  const [productoAFusionar, setProductoAFusionar] = useState<ProductoCuradorDto | null>(null);
  const [productoAEditar, setProductoAEditar] = useState<ProductoCuradorDto | null>(null);

  const especies = useQuery({ queryKey: ["especies-curador"], queryFn: listarEspeciesCurador });
  const aportes = useQuery({
    queryKey: ["productos-aportes"],
    queryFn: () => listarProductosCurador({ soloAportes: true }),
  });

  const mutacionBorrar = useMutation({
    mutationFn: borrarProductoCurador,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["productos-aportes"] }),
  });

  return (
    <section className="panel">
      <div className="titulo-fila">
        <div>
          <h2>Bandeja de aportes no homologados</h2>
          <p className="muted" style={{ margin: 0, fontSize: "0.9rem" }}>
            Productos agregados por usuarios al registrar atenciones. Puedes homologarlos para hacerlos oficiales o fusionarlos con uno existente.
          </p>
        </div>
      </div>

      {aportes.isPending && <p className="muted" style={{ marginTop: "1rem" }}>Cargando aportes…</p>}
      {aportes.error && <p className="err" style={{ marginTop: "1rem" }}>{aportes.error.message}</p>}

      {aportes.data && aportes.data.length === 0 && (
        <div className="vacio" style={{ padding: "3rem 1rem" }}>
          <p className="muted">🎉 ¡Excelente! No hay aportes pendientes de revisión.</p>
        </div>
      )}

      {aportes.data && aportes.data.length > 0 && (
        <div className="tarjetas" style={{ marginTop: "1.25rem" }}>
          {aportes.data.map((ap) => (
            <article key={ap.productoID} className="panel tarjeta" style={{ borderLeft: "3px solid var(--alerta)" }}>
              <div className="tarjeta-cab">
                <h2>{ap.nombre}</h2>
                <span className="etiqueta neutra">{NOMBRES_TIPO_PRODUCTO[ap.tipo]}</span>
              </div>

              <dl className="datos" style={{ fontSize: "0.85rem" }}>
                <dt>Laboratorio</dt>
                <dd>{ap.laboratorio ?? "—"}</dd>
                <dt>Especie</dt>
                <dd>{ap.especieNombre ?? "Todas"}</dd>
                <dt>Aportado por</dt>
                <dd>{ap.creadoPorNombre ?? "Usuario anónimo"}</dd>
                <dt>Fecha de aporte</dt>
                <dd>{formatearFecha(ap.creadoUtc)}</dd>
                <dt>En uso en</dt>
                <dd>
                  <strong>{ap.cantidadUsos} atenciones clínicas</strong>
                </dd>
              </dl>

              <div className="acciones" style={{ marginTop: "auto", paddingTop: "0.5rem" }}>
                <button
                  type="button"
                  className="boton-primario boton-chico"
                  onClick={() => setProductoAEditar(ap)}
                >
                  ✓ Promover a oficial…
                </button>
                <button
                  type="button"
                  className="boton-chico"
                  onClick={() => setProductoAFusionar(ap)}
                >
                  🔗 Fusionar con…
                </button>
                <button
                  type="button"
                  className="boton-chico boton-peligro"
                  onClick={() => {
                    if (confirm(`¿Descartar '${ap.nombre}'?`)) {
                      mutacionBorrar.mutate(ap.productoID);
                    }
                  }}
                >
                  ✕ Descartar
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {productoAEditar && (
        <ModalFormularioProducto
          producto={productoAEditar}
          especies={especies.data ?? []}
          onCerrar={() => setProductoAEditar(null)}
          forzarCurado
        />
      )}

      {productoAFusionar && (
        <ModalFusionarProducto
          origen={productoAFusionar}
          onCerrar={() => setProductoAFusionar(null)}
        />
      )}
    </section>
  );
}

// ==========================================
// 3. PESTAÑA ESPECIES Y RAZAS
// ==========================================

function PestanaEspeciesRazas() {
  const [especieSeleccionadaID, setEspecieSeleccionadaID] = useState<number | null>(null);
  const [busquedaRaza, setBusquedaRaza] = useState("");

  // Modales
  const [modalEspecie, setModalEspecie] = useState<EspecieCuradorDto | "nueva" | null>(null);
  const [modalRaza, setModalRaza] = useState<RazaCuradorDto | "nueva" | null>(null);

  const especies = useQuery({ queryKey: ["especies-curador"], queryFn: listarEspeciesCurador });
  const razas = useQuery({
    queryKey: ["razas-curador", especieSeleccionadaID],
    queryFn: () => listarRazasCurador(especieSeleccionadaID ?? undefined),
  });

  const especieActiva = especies.data?.find((e) => e.especieID === especieSeleccionadaID);

  const razasFiltradas = (razas.data ?? []).filter((r) =>
    busquedaRaza.trim() === ""
      ? true
      : r.nombre.toLowerCase().includes(busquedaRaza.toLowerCase()) ||
        r.especieNombre.toLowerCase().includes(busquedaRaza.toLowerCase()),
  );

  return (
    <div className="fila-division" style={{ display: "grid", gridTemplateColumns: "minmax(18rem, 1fr) 2.2fr", gap: "1.25rem" }}>
      {/* Columna Especies */}
      <section className="panel">
        <div className="titulo-fila">
          <div>
            <h2>Especies ({especies.data?.length ?? 0})</h2>
            <p className="muted" style={{ fontSize: "0.8rem", margin: 0 }}>
              {especieSeleccionadaID ? "Clic en la especie seleccionada para ver todas" : "Selecciona una especie para filtrar sus razas"}
            </p>
          </div>
          <button
            type="button"
            className="boton-primario boton-chico"
            onClick={() => setModalEspecie("nueva")}
          >
            + Nueva especie
          </button>
        </div>

        <ul className="lista-seleccionable" style={{ listStyle: "none", margin: "1rem 0 0", padding: 0 }}>
          <li
            className={`item-seleccionable ${especieSeleccionadaID === null ? "activo" : ""}`}
            onClick={() => setEspecieSeleccionadaID(null)}
          >
            <div>
              <strong>🐾 Todas las especies</strong>
              <div className="muted" style={{ fontSize: "0.75rem" }}>
                Ver catálogo completo de razas
              </div>
            </div>
          </li>
          {especies.data?.map((esp) => {
            const esSeleccionada = esp.especieID === especieSeleccionadaID;
            return (
              <li
                key={esp.especieID}
                className={`item-seleccionable ${esSeleccionada ? "activo" : ""}`}
                onClick={() => setEspecieSeleccionadaID(esSeleccionada ? null : esp.especieID)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <strong>{esp.nombre}</strong>
                    {!esp.activo && <span className="etiqueta neutra" style={{ marginLeft: "0.5rem" }}>Inactiva</span>}
                    <div className="muted" style={{ fontSize: "0.75rem" }}>
                      {esp.cantidadRazas} razas · {esp.cantidadMascotas} mascotas
                    </div>
                  </div>
                  <button
                    type="button"
                    className="boton-chico"
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalEspecie(esp);
                    }}
                  >
                    Editar
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Columna Razas */}
      <section className="panel">
        <div className="titulo-fila">
          <div>
            <h2>
              Razas {especieActiva ? `de ${especieActiva.nombre}` : "(Todas)"} ({razasFiltradas.length}{busquedaRaza ? ` de ${razas.data?.length ?? 0}` : ""})
            </h2>
          </div>
          {especieSeleccionadaID && (
            <button
              type="button"
              className="boton-primario boton-chico"
              onClick={() => setModalRaza("nueva")}
            >
              + Nueva raza en {especieActiva?.nombre}
            </button>
          )}
        </div>

        {/* Buscador de raza */}
        <div style={{ marginTop: "0.85rem" }}>
          <input
            type="text"
            placeholder="🔍 Buscar raza (ej. Pastor, Persa, Akita, Bulldog, Mestizo…)"
            value={busquedaRaza}
            onChange={(e) => setBusquedaRaza(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>

        {razas.isPending && <p className="muted" style={{ marginTop: "1rem" }}>Cargando razas…</p>}

        <div style={{ maxHeight: "36rem", overflowY: "auto", marginTop: "0.85rem" }}>
          <table className="tabla-datos">
            <thead>
              <tr>
                <th>Raza</th>
                <th>Especie</th>
                <th>Mascotas</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {razasFiltradas.length === 0 && !razas.isPending ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "1.5rem" }} className="muted">
                    No se encontraron razas con ese criterio.
                  </td>
                </tr>
              ) : (
                razasFiltradas.map((r) => (
                  <tr key={r.razaID}>
                    <td>
                      <strong>{r.nombre}</strong>
                    </td>
                    <td>{r.especieNombre}</td>
                    <td>{r.cantidadMascotas}</td>
                    <td>{r.activo ? "Activa" : <span className="muted">Inactiva</span>}</td>
                    <td>
                      <button
                        type="button"
                        className="boton-chico"
                        onClick={() => setModalRaza(r)}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalEspecie && (
        <ModalFormularioEspecie
          especie={modalEspecie === "nueva" ? null : modalEspecie}
          onCerrar={() => setModalEspecie(null)}
        />
      )}

      {modalRaza && (
        <ModalFormularioRaza
          raza={modalRaza === "nueva" ? null : modalRaza}
          especieIDDefecto={especieSeleccionadaID ?? especies.data?.[0]?.especieID ?? 1}
          especies={especies.data ?? []}
          onCerrar={() => setModalRaza(null)}
        />
      )}
    </div>
  );
}

// ==========================================
// 4. PESTAÑA COLORES Y TIPOS DE VISITA
// ==========================================

function PestanaColoresYTipos() {
  const [modalColor, setModalColor] = useState<ColorCuradorDto | "nuevo" | null>(null);
  const [modalTipoVisita, setModalTipoVisita] = useState<TipoVisitaCuradorDto | "nuevo" | null>(null);

  const colores = useQuery({ queryKey: ["colores-curador"], queryFn: listarColoresCurador });
  const tiposVisita = useQuery({ queryKey: ["tipos-visita-curador"], queryFn: listarTiposVisitaCurador });

  return (
    <div className="fila-division" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
      {/* Colores */}
      <section className="panel">
        <div className="titulo-fila">
          <h2>Colores de pelaje ({colores.data?.length ?? 0})</h2>
          <button
            type="button"
            className="boton-primario boton-chico"
            onClick={() => setModalColor("nuevo")}
          >
            + Nuevo color
          </button>
        </div>

        <div className="tabla-responsive" style={{ marginTop: "1rem" }}>
          <table className="tabla-datos">
            <thead>
              <tr>
                <th>Color</th>
                <th>Orden</th>
                <th>Mascotas</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {colores.data?.map((c) => (
                <tr key={c.colorID}>
                  <td>
                    <strong>{c.nombre}</strong>
                  </td>
                  <td>{c.orden}</td>
                  <td>{c.cantidadMascotas}</td>
                  <td>{c.activo ? "Activo" : <span className="muted">Inactivo</span>}</td>
                  <td>
                    <button
                      type="button"
                      className="boton-chico"
                      onClick={() => setModalColor(c)}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Tipos de Visita */}
      <section className="panel">
        <div className="titulo-fila">
          <h2>Tipos de visita ({tiposVisita.data?.length ?? 0})</h2>
          <button
            type="button"
            className="boton-primario boton-chico"
            onClick={() => setModalTipoVisita("nuevo")}
          >
            + Nuevo tipo
          </button>
        </div>

        <div className="tabla-responsive" style={{ marginTop: "1rem" }}>
          <table className="tabla-datos">
            <thead>
              <tr>
                <th>Tipo de atención</th>
                <th>Orden</th>
                <th>Visitas</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {tiposVisita.data?.map((tv) => (
                <tr key={tv.tipoVisitaID}>
                  <td>
                    <strong>{tv.nombre}</strong>
                  </td>
                  <td>{tv.orden}</td>
                  <td>{tv.cantidadVisitas}</td>
                  <td>{tv.activo ? "Activo" : <span className="muted">Inactivo</span>}</td>
                  <td>
                    <button
                      type="button"
                      className="boton-chico"
                      onClick={() => setModalTipoVisita(tv)}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {modalColor && (
        <ModalFormularioColor
          color={modalColor === "nuevo" ? null : modalColor}
          onCerrar={() => setModalColor(null)}
        />
      )}

      {modalTipoVisita && (
        <ModalFormularioTipoVisita
          tipoVisita={modalTipoVisita === "nuevo" ? null : modalTipoVisita}
          onCerrar={() => setModalTipoVisita(null)}
        />
      )}
    </div>
  );
}

// ==========================================
// MODALES Y FORMULARIOS AUXILIARES
// ==========================================

function ModalFormularioProducto({
  producto,
  especies,
  onCerrar,
  forzarCurado,
}: {
  producto: ProductoCuradorDto | null;
  especies: EspecieCuradorDto[];
  onCerrar: () => void;
  forzarCurado?: boolean;
}) {
  const qc = useQueryClient();
  const esEdicion = Boolean(producto);

  const [tipo, setTipo] = useState<TipoProducto>(producto?.tipo ?? TipoProducto.Vacuna);
  const [nombre, setNombre] = useState(producto?.nombre ?? "");
  const [laboratorio, setLaboratorio] = useState(producto?.laboratorio ?? "");
  const [especieID, setEspecieID] = useState<number | "">(producto?.especieID ?? "");
  const [claveSerie, setClaveSerie] = useState(producto?.claveSerie ?? "");
  const [periodicidadCantidad, setPeriodicidadCantidad] = useState<number | "">(
    producto?.periodicidadCantidad ?? "",
  );
  const [periodicidadUnidad, setPeriodicidadUnidad] = useState<UnidadPeriodo | "">(
    producto?.periodicidadUnidad ?? "",
  );
  const [esCurado, setEsCurado] = useState(forzarCurado ? true : producto?.esCurado ?? true);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (d: GuardarProductoCuradorRequest) =>
      esEdicion
        ? actualizarProductoCurador(producto!.productoID, d)
        : crearProductoCurador(d).then(() => {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["productos-curador"] });
      qc.invalidateQueries({ queryKey: ["productos-aportes"] });
      onCerrar();
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    guardar.mutate({
      tipo,
      nombre: nombre.trim(),
      laboratorio: laboratorio.trim() || null,
      especieID: typeof especieID === "number" ? especieID : null,
      claveSerie: claveSerie.trim().toUpperCase() || null,
      periodicidadCantidad: typeof periodicidadCantidad === "number" ? periodicidadCantidad : null,
      periodicidadUnidad: typeof periodicidadUnidad === "number" ? periodicidadUnidad : null,
      esCurado,
    });
  };

  return (
    <div className="modal-fondo">
      <div className="modal-caja panel">
        <h2>{esEdicion && producto ? `Editar producto: ${producto.nombre}` : "Nuevo producto oficial"}</h2>

        <form onSubmit={handleSubmit} className="formulario" style={{ marginTop: "1rem" }}>
          <div className="fila">
            <Campo etiqueta="Tipo de producto" requerido>
              <select
                value={tipo}
                onChange={(e) => setTipo(Number(e.target.value) as TipoProducto)}
              >
                <option value={TipoProducto.Vacuna}>Vacuna</option>
                <option value={TipoProducto.AntiparasitarioInterno}>Antiparasitario interno</option>
                <option value={TipoProducto.AntiparasitarioExterno}>Antiparasitario externo</option>
                <option value={TipoProducto.Medicamento}>Medicamento</option>
              </select>
            </Campo>

            <Campo etiqueta="Nombre comercial" requerido>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
              />
            </Campo>
          </div>

          <div className="fila">
            <Campo etiqueta="Laboratorio / Fabricante">
              <input
                type="text"
                placeholder="Ej: Zoetis, MSD, Boehringer…"
                value={laboratorio}
                onChange={(e) => setLaboratorio(e.target.value)}
              />
            </Campo>

            <Campo etiqueta="Especie aplicable">
              <select
                value={especieID}
                onChange={(e) => setEspecieID(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">Aplica a todas las especies</option>
                {especies.map((esp) => (
                  <option key={esp.especieID} value={esp.especieID}>
                    {esp.nombre}
                  </option>
                ))}
              </select>
            </Campo>
          </div>

          <div className="fila">
            <Campo
              etiqueta="Clave de serie (para recordatorios continuos)"
              ayuda="Ej: VAC_ANTIRRABICA, ANTIPAR_EXT, VAC_SEXTUPLE"
            >
              <input
                type="text"
                placeholder="Ej: ANTIPAR_EXT"
                value={claveSerie}
                onChange={(e) => setClaveSerie(e.target.value)}
              />
            </Campo>

            <Campo etiqueta="Periodicidad de repetición">
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  type="number"
                  min="1"
                  placeholder="Ej: 1, 3, 12"
                  value={periodicidadCantidad}
                  onChange={(e) =>
                    setPeriodicidadCantidad(e.target.value ? Number(e.target.value) : "")
                  }
                  style={{ width: "5rem" }}
                />
                <select
                  value={periodicidadUnidad}
                  onChange={(e) =>
                    setPeriodicidadUnidad(
                      e.target.value ? (Number(e.target.value) as UnidadPeriodo) : "",
                    )
                  }
                >
                  <option value="">— Unidad —</option>
                  <option value={UnidadPeriodo.Dias}>Días</option>
                  <option value={UnidadPeriodo.Meses}>Meses</option>
                  <option value={UnidadPeriodo.Anios}>Años</option>
                </select>
              </div>
            </Campo>
          </div>

          <div style={{ marginTop: "0.5rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={esCurado}
                onChange={(e) => setEsCurado(e.target.checked)}
                style={{ width: "auto" }}
              />
              <span>
                <strong>Producto oficial verificado</strong> (visible para todos los usuarios)
              </span>
            </label>
          </div>

          {error && <p className="err">{error}</p>}

          <div className="acciones" style={{ marginTop: "1rem" }}>
            <button type="submit" className="boton-primario" disabled={guardar.isPending}>
              {guardar.isPending ? "Guardando…" : "Guardar producto"}
            </button>
            <button type="button" onClick={onCerrar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalFusionarProducto({
  origen,
  onCerrar,
}: {
  origen: ProductoCuradorDto;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  const [busqueda, setBusqueda] = useState("");
  const [destinoID, setDestinoID] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const candidatos = useQuery({
    queryKey: ["productos-candidatos-fusion", { q: busqueda, tipo: origen.tipo }],
    queryFn: () =>
      listarProductosCurador({
        tipo: origen.tipo,
        q: busqueda.trim() || undefined,
      }),
  });

  const mutacionFusion = useMutation({
    mutationFn: fusionarProducto,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["productos-aportes"] });
      qc.invalidateQueries({ queryKey: ["productos-curador"] });
      alert(`Se fusionó correctamente. ${res.itemsReasignados} atenciones clínicas fueron reasignadas al producto oficial.`);
      onCerrar();
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleConfirmarFusion = () => {
    if (!destinoID) {
      setError("Debes seleccionar un producto de destino.");
      return;
    }
    mutacionFusion.mutate({
      origenProductoID: origen.productoID,
      destinoProductoID: destinoID,
    });
  };

  return (
    <div className="modal-fondo">
      <div className="modal-caja panel">
        <h2>Fusionar producto no homologado</h2>
        <p className="muted">
          Se reasignarán todas las atenciones que usen <strong>{origen.nombre}</strong> ({origen.cantidadUsos} usos) hacia el producto oficial que elijas, y luego se eliminará el duplicado.
        </p>

        <div style={{ marginTop: "1rem" }}>
          <Campo etiqueta="Buscar producto oficial de destino">
            <input
              type="text"
              placeholder="Escribe el nombre del producto oficial…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </Campo>
        </div>

        <div style={{ maxHeight: "15rem", overflowY: "auto", margin: "1rem 0", border: "1px solid var(--rule)", borderRadius: "0.35rem" }}>
          {candidatos.data
            ?.filter((p) => p.productoID !== origen.productoID)
            .map((c) => (
              <label
                key={c.productoID}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.5rem 0.75rem",
                  borderBottom: "1px solid var(--rule)",
                  cursor: "pointer",
                  background: destinoID === c.productoID ? "var(--accent-suave)" : "transparent",
                }}
              >
                <div>
                  <input
                    type="radio"
                    name="destino"
                    checked={destinoID === c.productoID}
                    onChange={() => setDestinoID(c.productoID)}
                    style={{ marginRight: "0.5rem", width: "auto" }}
                  />
                  <strong>{c.nombre}</strong>
                  {c.laboratorio && <span className="muted"> · {c.laboratorio}</span>}
                </div>
                <span className="etiqueta neutra">{c.esCurado ? "Oficial" : "Aporte"}</span>
              </label>
            ))}
        </div>

        {error && <p className="err">{error}</p>}

        <div className="acciones">
          <button
            type="button"
            className="boton-primario"
            disabled={!destinoID || mutacionFusion.isPending}
            onClick={handleConfirmarFusion}
          >
            {mutacionFusion.isPending ? "Fusionando…" : "Confirmar y fusionar"}
          </button>
          <button type="button" onClick={onCerrar}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalFormularioEspecie({
  especie,
  onCerrar,
}: {
  especie: EspecieCuradorDto | null;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  const esEdicion = Boolean(especie);
  const [nombre, setNombre] = useState(especie?.nombre ?? "");
  const [orden, setOrden] = useState(especie?.orden ?? 1);
  const [activo, setActivo] = useState(especie?.activo ?? true);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (d: GuardarEspecieRequest) =>
      esEdicion ? actualizarEspecieCurador(especie!.especieID, d) : crearEspecieCurador(d).then(() => {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["especies-curador"] });
      qc.invalidateQueries({ queryKey: ["catalogos"] });
      onCerrar();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="modal-fondo">
      <div className="modal-caja panel" style={{ maxWidth: "25rem" }}>
        <h2>{esEdicion ? `Editar especie` : "Nueva especie"}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate({ nombre: nombre.trim(), orden, activo });
          }}
          className="formulario"
          style={{ marginTop: "1rem" }}
        >
          <Campo etiqueta="Nombre de la especie" requerido>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </Campo>
          <Campo etiqueta="Orden de presentación">
            <input type="number" value={orden} onChange={(e) => setOrden(Number(e.target.value))} />
          </Campo>
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>Especie activa</span>
          </label>
          {error && <p className="err">{error}</p>}
          <div className="acciones">
            <button type="submit" className="boton-primario" disabled={guardar.isPending}>
              Guardar
            </button>
            <button type="button" onClick={onCerrar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalFormularioRaza({
  raza,
  especieIDDefecto,
  especies,
  onCerrar,
}: {
  raza: RazaCuradorDto | null;
  especieIDDefecto: number;
  especies: EspecieCuradorDto[];
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  const esEdicion = Boolean(raza);
  const [especieID, setEspecieID] = useState(raza?.especieID ?? especieIDDefecto);
  const [nombre, setNombre] = useState(raza?.nombre ?? "");
  const [orden, setOrden] = useState(raza?.orden ?? 1);
  const [activo, setActivo] = useState(raza?.activo ?? true);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (d: GuardarRazaRequest) =>
      esEdicion ? actualizarRazaCurador(raza!.razaID, d) : crearRazaCurador(d).then(() => {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["razas-curador"] });
      qc.invalidateQueries({ queryKey: ["catalogos"] });
      onCerrar();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="modal-fondo">
      <div className="modal-caja panel" style={{ maxWidth: "25rem" }}>
        <h2>{esEdicion ? `Editar raza` : "Nueva raza"}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate({ especieID, nombre: nombre.trim(), orden, activo });
          }}
          className="formulario"
          style={{ marginTop: "1rem" }}
        >
          <Campo etiqueta="Especie" requerido>
            <select value={especieID} onChange={(e) => setEspecieID(Number(e.target.value))}>
              {especies.map((esp) => (
                <option key={esp.especieID} value={esp.especieID}>
                  {esp.nombre}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Nombre de la raza" requerido>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </Campo>
          <Campo etiqueta="Orden de presentación">
            <input type="number" value={orden} onChange={(e) => setOrden(Number(e.target.value))} />
          </Campo>
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>Raza activa</span>
          </label>
          {error && <p className="err">{error}</p>}
          <div className="acciones">
            <button type="submit" className="boton-primario" disabled={guardar.isPending}>
              Guardar
            </button>
            <button type="button" onClick={onCerrar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalFormularioColor({
  color,
  onCerrar,
}: {
  color: ColorCuradorDto | null;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  const esEdicion = Boolean(color);
  const [nombre, setNombre] = useState(color?.nombre ?? "");
  const [orden, setOrden] = useState(color?.orden ?? 1);
  const [activo, setActivo] = useState(color?.activo ?? true);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (d: GuardarColorRequest) =>
      esEdicion ? actualizarColorCurador(color!.colorID, d) : crearColorCurador(d).then(() => {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["colores-curador"] });
      qc.invalidateQueries({ queryKey: ["catalogos"] });
      onCerrar();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="modal-fondo">
      <div className="modal-caja panel" style={{ maxWidth: "25rem" }}>
        <h2>{esEdicion ? `Editar color` : "Nuevo color"}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate({ nombre: nombre.trim(), orden, activo });
          }}
          className="formulario"
          style={{ marginTop: "1rem" }}
        >
          <Campo etiqueta="Nombre del color" requerido>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </Campo>
          <Campo etiqueta="Orden de presentación">
            <input type="number" value={orden} onChange={(e) => setOrden(Number(e.target.value))} />
          </Campo>
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>Color activo</span>
          </label>
          {error && <p className="err">{error}</p>}
          <div className="acciones">
            <button type="submit" className="boton-primario" disabled={guardar.isPending}>
              Guardar
            </button>
            <button type="button" onClick={onCerrar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalFormularioTipoVisita({
  tipoVisita,
  onCerrar,
}: {
  tipoVisita: TipoVisitaCuradorDto | null;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  const esEdicion = Boolean(tipoVisita);
  const [nombre, setNombre] = useState(tipoVisita?.nombre ?? "");
  const [orden, setOrden] = useState(tipoVisita?.orden ?? 1);
  const [activo, setActivo] = useState(tipoVisita?.activo ?? true);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (d: GuardarTipoVisitaRequest) =>
      esEdicion
        ? actualizarTipoVisitaCurador(tipoVisita!.tipoVisitaID, d)
        : crearTipoVisitaCurador(d).then(() => {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tipos-visita-curador"] });
      qc.invalidateQueries({ queryKey: ["catalogos"] });
      onCerrar();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="modal-fondo">
      <div className="modal-caja panel" style={{ maxWidth: "25rem" }}>
        <h2>{esEdicion ? `Editar tipo de visita` : "Nuevo tipo de visita"}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate({ nombre: nombre.trim(), orden, activo });
          }}
          className="formulario"
          style={{ marginTop: "1rem" }}
        >
          <Campo etiqueta="Nombre del tipo de visita" requerido>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </Campo>
          <Campo etiqueta="Orden de presentación">
            <input type="number" value={orden} onChange={(e) => setOrden(Number(e.target.value))} />
          </Campo>
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>Tipo activo</span>
          </label>
          {error && <p className="err">{error}</p>}
          <div className="acciones">
            <button type="submit" className="boton-primario" disabled={guardar.isPending}>
              Guardar
            </button>
            <button type="button" onClick={onCerrar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
